/**
 * Chamada de voz do painel — UMA por vez e GLOBAL (provider no AppShell).
 *
 * Antes a chamada vivia em quem a abria: a de saída no ThreadPanel e a
 * entrante no IncomingCallOverlay, cada uma com seu `useVoipCall` e um modal
 * de tela cheia. O modal travava o painel (não dava pra responder o cliente
 * durante a ligação); a de saída caía ao trocar de página (o ThreadPanel
 * desmontava e o hook fechava o áudio) e sumia da tela, com a ligação no ar,
 * quando o atendimento fechava; e atender uma entrante com outra no ar
 * deixava duas ligações abertas ao mesmo tempo.
 *
 * Agora o hook vive aqui, acima das rotas, e a ligação aparece na janelinha
 * flutuante (CallWindow), que não bloqueia nada e sobrevive à navegação.
 */
import { type ReactNode, createContext, useCallback, useContext, useState } from 'react';
import { CallWindow, type CallWindowPos, isCallConnected } from './CallWindow';
import { type VoipState, useVoipCall } from './useVoipCall';

/** Tem ligação em andamento (inclui o "preparando microfone"). */
export function isCallLive(state: VoipState): boolean {
  return state === 'requesting' || isCallConnected(state);
}

interface CallSessionValue {
  /** true enquanto há ligação em andamento — não dá pra iniciar outra. */
  busy: boolean;
  /** Liga pro contato do atendimento (o "Ligar" do cabeçalho da conversa). */
  startOutbound: (ticketId: number, contactName: string) => void;
  /** Atende a chamada entrante. Com outra no ar, encerra ela antes. */
  answerIncoming: (callId: string, contactName: string) => void;
}

const CallSessionContext = createContext<CallSessionValue | null>(null);

export function CallSessionProvider({ children }: { children: ReactNode }) {
  const voip = useVoipCall();
  const { state, start, answer, end, reset } = voip;
  // Quem está na linha (nome capturado ao ligar/atender — não acompanha a
  // conversa aberta, que pode mudar durante a ligação). null = sem janela.
  const [session, setSession] = useState<{ contactName: string; answering: boolean } | null>(null);
  const [pos, setPos] = useState<CallWindowPos | null>(null);

  const startOutbound = useCallback(
    (ticketId: number, contactName: string) => {
      if (isCallLive(state)) return;
      reset(); // limpa "encerrada"/erro da ligação anterior
      setPos(null);
      setSession({ contactName, answering: false });
      void start(ticketId);
    },
    [state, start, reset],
  );

  const answerIncoming = useCallback(
    (callId: string, contactName: string) => {
      // Um hook = um áudio: sem encerrar antes, o WS da ligação atual ficava
      // órfão (o `begin` sobrescreve as refs).
      if (isCallConnected(state)) end();
      reset();
      setPos(null);
      setSession({ contactName, answering: true });
      void answer(callId);
    },
    [state, answer, end, reset],
  );

  const close = useCallback(() => {
    if (isCallConnected(state)) end();
    reset();
    setSession(null);
    setPos(null);
  }, [state, end, reset]);

  return (
    <CallSessionContext.Provider value={{ busy: isCallLive(state), startOutbound, answerIncoming }}>
      {children}
      {session && (
        <CallWindow
          call={voip}
          contactName={session.contactName}
          answering={session.answering}
          pos={pos}
          onMove={setPos}
          onClose={close}
        />
      )}
    </CallSessionContext.Provider>
  );
}

export function useCallSession(): CallSessionValue {
  const ctx = useContext(CallSessionContext);
  if (!ctx) throw new Error('useCallSession fora do CallSessionProvider');
  return ctx;
}
